// packages/core/src/replay/MarketSeries.ts
// ─────────────────────────────────────────────────────────────────────────
// Accessors over the generated historical dataset (MarketData.ts).
// Calendar math, per-asset total-return series (blends built lazily and
// memoized), and CPI helpers. Pure — no state beyond memoized blends.
// ─────────────────────────────────────────────────────────────────────────

import {
	MARKET_DATA_START,
	MARKET_DATA_MONTHS,
	MARKET_DATA_END,
	STOCKS_TR,
	BONDS_TR,
	CASH_TR,
	CPI,
} from './MarketData';
import type { ReplayAssetId, ReplayYearMonth } from './ReplayTypes';

export { MARKET_DATA_START, MARKET_DATA_END, MARKET_DATA_MONTHS };

/** Serial month number (year*12 + zero-based month) for ordering math. */
function serial(ym: ReplayYearMonth): number {
	return ym.year * 12 + (ym.month - 1);
}

const START_SERIAL = serial(MARKET_DATA_START);

/**
 * Global dataset index for a calendar month. Throws when the month falls
 * outside the dataset — scenario windows are validated at authoring time,
 * so an out-of-range lookup is a programming error, not a user error.
 */
export function marketIndex(ym: ReplayYearMonth): number {
	const i = serial(ym) - START_SERIAL;
	if (i < 0 || i >= MARKET_DATA_MONTHS) {
		throw new Error(
			`Market Replay: ${ym.year}-${ym.month} is outside the dataset ` +
				`(${MARKET_DATA_START.year}-${MARKET_DATA_START.month} → ` +
				`${MARKET_DATA_END.year}-${MARKET_DATA_END.month}).`,
		);
	}
	return i;
}

/** Calendar month at a global dataset index. */
export function marketMonthAt(index: number): ReplayYearMonth {
	const s = START_SERIAL + index;
	return { year: Math.floor(s / 12), month: (s % 12) + 1 };
}

/** Add n months to a calendar month. */
export function addMonths(ym: ReplayYearMonth, n: number): ReplayYearMonth {
	const s = serial(ym) + n;
	return { year: Math.floor(s / 12), month: (s % 12) + 1 };
}

/** True when a whole window [start, start+months] fits in the dataset. */
export function windowFits(start: ReplayYearMonth, months: number): boolean {
	const i = serial(start) - START_SERIAL;
	return i >= 0 && i + months < MARKET_DATA_MONTHS;
}

// ─── Blend construction (lazy, memoized) ─────────────────────────────────
// A blend is monthly-rebalanced: each month's blend return is the weighted
// average of that month's stock and bond returns. This is the standard
// textbook construction and matches how target-mix funds behave.

const blendCache = new Map<number, readonly number[]>();

function blendSeries(stockWeight: number): readonly number[] {
	const cached = blendCache.get(stockWeight);
	if (cached) return cached;
	const out: number[] = new Array<number>(MARKET_DATA_MONTHS);
	out[0] = 1;
	for (let i = 1; i < MARKET_DATA_MONTHS; i++) {
		const rs = STOCKS_TR[i] / STOCKS_TR[i - 1] - 1;
		const rb = BONDS_TR[i] / BONDS_TR[i - 1] - 1;
		out[i] = out[i - 1] * (1 + stockWeight * rs + (1 - stockWeight) * rb);
	}
	blendCache.set(stockWeight, out);
	return out;
}

/** The cumulative nominal total-return index for an asset. */
export function assetSeries(asset: ReplayAssetId): readonly number[] {
	switch (asset) {
		case 'us-stocks':
			return STOCKS_TR;
		case 'us-bonds':
			return BONDS_TR;
		case 'cash':
			return CASH_TR;
		case 'blend-80-20':
			return blendSeries(0.8);
		case 'blend-60-40':
			return blendSeries(0.6);
	}
}

/** Cash series export for the sell-to-cash branch of the engine. */
export function cashSeries(): readonly number[] {
	return CASH_TR;
}

/** CPI level at a global dataset index. */
export function cpiAt(index: number): number {
	return CPI[index];
}

const MONTH_NAMES = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
] as const;

/** "Mar 2009" — the standard replay month label. */
export function formatReplayMonth(ym: ReplayYearMonth): string {
	return `${MONTH_NAMES[ym.month - 1]} ${ym.year}`;
}
